import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit2, Trash2, Tag, Palette } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import useTagStore from '../../store/tagStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Modal } from '../../components/ui/modal';

const TAG_COLORS = [
  '#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16', '#22C55E',
  '#10B981', '#14B8A6', '#06B6D4', '#0EA5E9', '#3B82F6', '#6366F1',
  '#8B5CF6', '#A855F7', '#C026D2', '#DB2777', '#E11D48', '#6B7280'
];

const TagManagement = () => {
  const { userProfile } = useAuthStore();
  const { tags, subscribeToTags, createTag, updateTag, deleteTag, loading, error } = useTagStore();
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: TAG_COLORS[0]
  });
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (userProfile?.familyId) {
      subscribeToTags(userProfile.familyId);
    }
  }, [userProfile?.familyId, subscribeToTags]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: TAG_COLORS[0]
    });
    setFormError('');
  };

  const handleCreateTag = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setFormError('Tag name is required');
      return;
    }

    const result = await createTag(userProfile.familyId, userProfile.uid, {
      name: formData.name.trim(),
      description: formData.description.trim(),
      color: formData.color
    });

    if (result.success) {
      setShowCreateModal(false);
      resetForm();
    } else {
      setFormError(result.error);
    }
  };

  const handleEditTag = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setFormError('Tag name is required');
      return;
    }

    const result = await updateTag(userProfile.familyId, selectedTag.id, {
      name: formData.name.trim(),
      description: formData.description.trim(),
      color: formData.color
    });

    if (result.success) {
      setShowEditModal(false);
      setSelectedTag(null);
      resetForm();
    } else {
      setFormError(result.error);
    }
  };

  const handleDeleteTag = async () => {
    const result = await deleteTag(userProfile.familyId, selectedTag.id);
    
    if (result.success) {
      setShowDeleteModal(false);
      setSelectedTag(null);
    }
  };

  const openEditModal = (tag) => {
    setSelectedTag(tag);
    setFormData({
      name: tag.name,
      description: tag.description || '',
      color: tag.color
    });
    setFormError('');
    setShowEditModal(true);
  };

  const openDeleteModal = (tag) => {
    setSelectedTag(tag);
    setShowDeleteModal(true);
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  if (loading && tags.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Link to="/settings">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tag Management</h1>
            <p className="text-gray-600">Loading tags...</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/settings">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tag Management</h1>
            <p className="text-gray-600">Create and manage expense tags for better organization</p>
          </div>
        </div>
        <Button onClick={openCreateModal}>
          <Plus className="h-4 w-4 mr-2" />
          Create Tag
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Your Tags</CardTitle>
          <CardDescription>
            {tags.length} tag{tags.length !== 1 ? 's' : ''} created
          </CardDescription>
        </CardHeader>
        <CardContent>
          {tags.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                <Tag className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No tags yet</h3>
              <p className="text-gray-600 mb-4">
                Create your first tag to start organizing your expenses.
              </p>
              <Button onClick={openCreateModal}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Tag
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tags.map((tag) => (
                <div
                  key={tag.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded-full flex-shrink-0"
                        style={{ backgroundColor: tag.color }}
                      />
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                          {tag.name}
                        </h3>
                        {tag.description && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                            {tag.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 ml-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(tag)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit2 className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteModal(tag)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Used {tag.usageCount || 0} times</span>
                    <span>Created {new Date(tag.createdAt?.toDate ? tag.createdAt.toDate() : tag.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Tag Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Tag"
      >
        <form onSubmit={handleCreateTag} className="space-y-4">
          {formError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
              {formError}
            </div>
          )}
          
          <div>
            <label htmlFor="tagName" className="block text-sm font-medium text-gray-700 mb-1">
              Tag Name *
            </label>
            <Input
              id="tagName"
              type="text"
              placeholder="e.g., Marriage, Vacation, Emergency"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={formError && !formData.name.trim() ? 'border-red-500' : ''}
            />
          </div>

          <div>
            <label htmlFor="tagDescription" className="block text-sm font-medium text-gray-700 mb-1">
              Description (Optional)
            </label>
            <textarea
              id="tagDescription"
              rows={3}
              placeholder="Brief description of what this tag represents..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Palette className="h-4 w-4 inline mr-1" />
              Color
            </label>
            <div className="grid grid-cols-9 gap-2">
              {TAG_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData({ ...formData, color })}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    formData.color === color 
                      ? 'border-gray-900 scale-110' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? 'Creating...' : 'Create Tag'}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setShowCreateModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Tag Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Tag"
      >
        <form onSubmit={handleEditTag} className="space-y-4">
          {formError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
              {formError}
            </div>
          )}
          
          <div>
            <label htmlFor="editTagName" className="block text-sm font-medium text-gray-700 mb-1">
              Tag Name *
            </label>
            <Input
              id="editTagName"
              type="text"
              placeholder="e.g., Marriage, Vacation, Emergency"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={formError && !formData.name.trim() ? 'border-red-500' : ''}
            />
          </div>

          <div>
            <label htmlFor="editTagDescription" className="block text-sm font-medium text-gray-700 mb-1">
              Description (Optional)
            </label>
            <textarea
              id="editTagDescription"
              rows={3}
              placeholder="Brief description of what this tag represents..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Palette className="h-4 w-4 inline mr-1" />
              Color
            </label>
            <div className="grid grid-cols-9 gap-2">
              {TAG_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData({ ...formData, color })}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    formData.color === color 
                      ? 'border-gray-900 scale-110' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? 'Updating...' : 'Update Tag'}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setShowEditModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Tag"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to delete the tag "{selectedTag?.name}"? This action cannot be undone.
          </p>
          {selectedTag?.usageCount > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-2 rounded-md text-sm">
              <strong>Warning:</strong> This tag is currently used by {selectedTag.usageCount} expense{selectedTag.usageCount !== 1 ? 's' : ''}. 
              You cannot delete a tag that is being used.
            </div>
          )}
          <div className="flex space-x-3 pt-4">
            <Button 
              onClick={handleDeleteTag} 
              disabled={loading || (selectedTag?.usageCount > 0)}
              variant="destructive"
              className="flex-1"
            >
              {loading ? 'Deleting...' : 'Delete Tag'}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setShowDeleteModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TagManagement;
